import { useCallback, useEffect, useRef, useState } from 'react';
import { usePasskey } from './usePasskey.js';
import { isMobileDevice } from '../lib/isMobileDevice.js';
import { hasPasskeyFlag } from '../lib/passkeyFlag.js';
import { isUnlockedThisLoad } from '../lib/appLockState.js';

export const LOCK_AFTER_MS = 30_000;

/**
 * Verrou Face ID, actif uniquement sur mobile avec une passkey enregistrée.
 * cold : démarrage à froid pas encore déverrouillé (les enfants ne doivent pas être rendus).
 * covered : app en arrière-plan, contenu masqué pour l'aperçu du sélecteur d'apps.
 */
export function useAppLock() {
  const { isSupported, login } = usePasskey();
  const isEnabled = useCallback(
    () => isSupported && isMobileDevice() && hasPasskeyFlag(),
    [isSupported]
  );

  const [cold, setCold] = useState(() => isEnabled() && !isUnlockedThisLoad());
  const [relocked, setRelocked] = useState(false);
  const [covered, setCovered] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const hiddenAt = useRef(null);

  const unlock = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setFailed(false);
    const result = await login();
    running.current = false;
    setBusy(false);
    if (result.status === 'ok') {
      setCold(false);
      setRelocked(false);
      setCovered(false);
    } else {
      setFailed(true);
    }
  }, [login]);

  useEffect(() => {
    if (cold) unlock();
    // Une seule tentative automatique au démarrage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        if (!isEnabled()) return;
        hiddenAt.current = Date.now();
        setCovered(true);
        return;
      }
      const since = hiddenAt.current;
      hiddenAt.current = null;
      if (since == null) return;
      if (Date.now() - since > LOCK_AFTER_MS && isEnabled()) {
        setRelocked(true);
        unlock();
      } else {
        setCovered(false);
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isEnabled, unlock]);

  return { cold, locked: cold || relocked, covered, failed, busy, unlock };
}
